/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IMenuItemData, INavigationMenuViewProps } from '@msdyn365-commerce-modules/navigation-menu';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { ISmweNavigationMenuProps } from '../definition-extensions/smwe-navigation-menu.ext.props.autogenerated';

interface INavigationState {
  parentMenu?: number;
  activeMenu?: number;
  mobileViewLabelText?: string;
  navOpen: boolean;
  sideNavOpened: boolean;
}
interface IMenuItemDataClass extends IMenuItemData {
  className?: string;
}

/**
 *
 * NavigationMenuView component
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<INavigationMenuViewProps & ISmweNavigationMenuProps<{}>, INavigationState> {
  private supportedLevel: number = 3;
  private currentLevel: number = 0;
  private menuNode: React.RefObject<HTMLUListElement>;
  private pathName: string = '';
  private activeClassName: string = 'selected';
  private activeMenuItem: IMenuItemData = {};
  private activeSubMenuItem: IMenuItemData = {};

  constructor(props: INavigationMenuViewProps & ISmweNavigationMenuProps<{}>) {
    super(props);
    this.menuNode = React.createRef();
    this.state = { activeMenu: undefined, mobileViewLabelText: '', parentMenu: undefined, navOpen: false, sideNavOpened: false };
    this._closeSubmenu = this._closeSubmenu.bind(this);
    this._onMobNavTrigger = this._onMobNavTrigger.bind(this);
  }

  public componentDidMount(): void {
    document.body && document.body.addEventListener('click', this._handleClickOutside);
  }

  public componentWillUnmount(): void {
    document.body && document.body.removeEventListener('click', this._handleClickOutside, false);
  }

  public render(): JSX.Element | null {
    const { isMobileView, MenuList, Navigation } = this.props;
    // get current pathname
    if (process.env.CURRENT_ENVIRONMENT === 'web') {
      this.pathName = window.location.pathname;
    } else {
      this.pathName = this.props.context.request.url.requestUrl.pathname;
    }

    this.props.menuItemData.forEach((item, index) => {
      const ItemPath = this._getPathNameFromURL(item.linkURL || '');
      item.subMenu && item.subMenu.forEach((subItem, subIndex) => {
        const subItemPath = this._getPathNameFromURL(subItem.linkURL || '');
        if (subItemPath === this.pathName) {
          this.activeMenuItem = item;
          this.activeSubMenuItem = subItem;
        }
      });
      if (ItemPath === this.pathName) {
        this.activeMenuItem = item;
      }
    });

    this.currentLevel = 1;
    return (
      <React.Fragment>
        <button
          onClick={this._onMobNavTrigger}
          className='ms-header__nav-icon msc-btn'
          aria-label='Menu'
          aria-expanded={this.state.sideNavOpened ? 'true' : 'false'}
          aria-haspopup='true'
        />
        <Module {...Navigation} id='NavMenu' className={classnames(Navigation.className, isMobileView && this.state.activeMenu !== undefined ? 'child' : 'parent', this.state.sideNavOpened ? 'nav-open' : 'nav-closed')}>
          <Node {...MenuList} ref={this.menuNode} role="menubar">
            {this._renderDisplay()}
          </Node>
        </Module>
      </React.Fragment>
    );
  }

  private _getPathNameFromURL(linkURL: string): string {
    let pathName = '';
    try {
      // if linkURL is a full url
      pathName = new URL(linkURL).pathname;
    } catch {
      pathName = linkURL.split('?')[0];
    }
    return pathName;
  }

  private _onMobNavTrigger(): void {
    this.setState(state => ({
      sideNavOpened: !state.sideNavOpened
    }));
  }

  private _renderDisplay(): JSX.Element[] {
    const { ListItem, menuItemData } = this.props;
    const menuItemList: JSX.Element[] = [];
    const isMobileView: boolean = this.props.isMobileView || false;
    const expandOnClick: boolean = this.props.config.expandOnClick || true;
    menuItemData.forEach((item: IMenuItemData, index: number) => {
      menuItemList.push(
        <Node key={index} {...ListItem} onClick={(isMobileView || expandOnClick) && this.state.navOpen ? this._closeSubmenu : false} onMouseLeave={isMobileView || expandOnClick ? false : this._closeSubmenu} role='presentation'>
          {this._createMenuItemList(item)}
        </Node>
      );
    });

    return menuItemList;
  }

  private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
    if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
      if (this.props.isMobileView) {
        return (
          <React.Fragment>
            {this._renderButtonMenuItem(menuItemData)}
            <Node className={`collapse subnav-menu ${menuItemData.id === this.state.activeMenu ? 'subnav-show' : 'subnav-hide'}`}>
              {this._renderSubMenu(menuItemData.subMenu, menuItemData.id)}
            </Node>
          </React.Fragment>);
      } else {
        return (
          <React.Fragment>
            {this._renderButtonMenuItem(menuItemData)}
            {this._renderSubMenu(menuItemData.subMenu, menuItemData.id)}
          </React.Fragment>
        );
      }
    } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
      return (this._renderLinkMenuItem(menuItemData));
    } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
      return (this._renderSpanMenuItem(menuItemData));
    }

    return null;
  }

  private _renderSubMenu(subMenus?: IMenuItemDataClass[], activeMenu?: number): JSX.Element | null {
    const { isMobileView, ListItem } = this.props;
    if (activeMenu && this.state.activeMenu !== activeMenu && !isMobileView) {
      return null;
    }

    if (!subMenus || subMenus.length === 0) {
      return null;
    }

    let levelClassName: string = '';
    const menuOptions = subMenus && subMenus.map((option: IMenuItemDataClass, idx: number) => {
      const hasOptions = (option.subMenu && option.subMenu.length > 0);
      let menuItem: JSX.Element | null;
      if (hasOptions && isMobileView) {
        menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
      } else {
        menuItem = (option.linkURL
          ? this._renderLinkMenuItem(option, idx)
          : this._renderSpanMenuItem(option)
        );
      }

      let subMenu;

      if (hasOptions) {
        this.currentLevel++;
        if (this.currentLevel <= this.supportedLevel - 1) {
          levelClassName = `level-${this.currentLevel.toString()}`;
          subMenu = this._renderSubMenu(option.subMenu, isMobileView ? option.id : undefined);
        }
      }

      return (
        <Node {...ListItem} key={option.id} className={classnames(ListItem.className, option.className)} role="presentation">
          {menuItem}
          {subMenu}
        </Node>
      );
    });

    return (this._renderMenu(levelClassName, menuOptions, activeMenu));
  }
  private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
    const { Button } = this.props;
    const isActive = (option === this.activeMenuItem) || (option === this.activeSubMenuItem);
    const activeClass = `${Button.className} ${isActive ? this.activeClassName : ''} ${this.state.activeMenu && this.state.activeMenu === option.id ?  'nav-open' : 'nav-closed'}`;
    const isMobileView: boolean = this.props.isMobileView || false;
    const expandOnClick: boolean = this.props.config.expandOnClick || true;

    return (
      <Node
        key={index}
        {...Button}
        className={activeClass}
        // onMouseDown={this._handleDropdownToggle(option, activeMenu)}
        // onFocus={!this.props.isMobileView ? this._handleFocus(option, activeMenu) : false}
        onClick={isMobileView || expandOnClick ? this._handleDropdownToggle(option, activeMenu) : () => this._mainNavClick(option)}
        onMouseEnter={isMobileView || expandOnClick ? false : this._handleDropdownToggle(option, activeMenu)}
        aria-haspopup={true}
        aria-expanded={this.state.activeMenu && this.state.activeMenu === option.id ? true : false}
        data-parent={activeMenu}
        role="menuitem"
        aria-current={isActive && 'page'}
      >
        {option.linkText}
      </Node>
    );
  }

  private _mainNavClick = (option: IMenuItemData) => {
    const link = option.linkURL;
    if (link) {
          window.location.href = link;
      }
  };

  private _renderLinkMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
    const { Link } = this.props;
    const isActive = (option === this.activeSubMenuItem) || (option === this.activeMenuItem);
    const activeClass = `${Link.className} ${isActive ? this.activeClassName : ''}`;

    return (
      <Node {...Link} className={activeClass} key={index} href={option.linkURL} role="menuitem" aria-current={isActive && 'page'}>
        {option.linkText}
      </Node>
    );
  }

  private _renderSpanMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
    const { Span } = this.props;
    return (
      <Node key={index} {...Span}>{option.linkText}</Node>
    );
  }

  private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number): JSX.Element | null {
    const { MenuList } = this.props;
    this.currentLevel = 1;
    return (
      <Node {...MenuList} className={classnames(MenuList.className, level)} role='menu'>
        {menuOptions}
      </Node>
    );
  }

  // private _handleFocus = (data: IMenuItemData, parentId?: number) => () => {
  //   if (!this.props.isMobileView) {
  //     this.setState({
  //       activeMenu: data.id!,
  //       parentMenu: parentId
  //     });
  //   }

  //   this._resetFocus();
  // };

  private _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
    const activeMenu =  (this.state.activeMenu && this.state.activeMenu === data.id!) ? undefined : data.id!;
    this.setState({
      activeMenu,
      parentMenu: parentId,
      navOpen: true
    });

    this._resetFocus();
  };

  private _resetFocus = () => {
    if (this.props.isMobileView) {
      setTimeout(() => {
        this.menuNode && this.menuNode.current && this.menuNode.current.focus();
      },         0);
    }
  };

  // tslint:disable-next-line:no-any
  private _handleClickOutside = (event: any) => {
    const burgerMenu:boolean = event.target.className ===  'ms-header__nav-icon msc-btn';
    if (this.menuNode.current && !this.menuNode.current.contains(event.target) && !burgerMenu && !(event.target instanceof HTMLButtonElement || event.target instanceof HTMLAnchorElement)) {
      this.setState({ navOpen: false, sideNavOpened: false });
    }
  };

  private _closeSubmenu(): void {
    if (!this.props.isMobileView) {
      this.setState({ activeMenu: undefined, mobileViewLabelText: '', navOpen: false });
    }
  }
}

export default NavigationMenuView;